import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, KeyRound } from 'lucide-react';
import { describeApiError, passwordApi } from '@/api';
import { useAuth } from '@/auth/AuthContext';
import { PasswordInput } from '@/components/PasswordInput';
import { ErrorAlert, Modal } from '@/components/Primitives';
import { useToast } from '@/lib/useToast';

/**
 * تغيير كلمة السر الإجباري في نافذة صغيرة (#64).
 *
 * كان المستخدم يُحوَّل إلى `/account` ويجد الحقل في أسفل الصفحة مؤطَّراً
 * بالأحمر، وهو فوق لا يرى شيئاً. الآن: أينما هبط بعد الدخول تظهر هذه
 * النافذة فوق الصفحة — لا تُغلق إلا بتغيير كلمة السر — وفيها الحقول
 * الثلاثة، وتناسب الهاتف والحاسوب لأنها النافذة نفسها في باقي الموقع.
 *
 * بعد النجاح يُطلب `/auth/me` من جديد ليسقط العلم في الجلسة فوراً، لا في
 * التحميل التالي.
 */
export function ForcedPasswordModal() {
  const { t } = useTranslation();
  const toast = useToast();
  const { refresh } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (next.length < 8) {
      setError(t('password.tooShort'));
      return;
    }
    if (next !== confirm) {
      setError(t('password.mismatch'));
      return;
    }
    setBusy(true);
    try {
      await passwordApi.changeOwn(current, next);
      toast.push(`${t('password.changed')} · ${t('password.othersSignedOut')}`);
      await refresh();
      // #136: الخادم كان يرفض كل طلب قبل التغيير، فالصفحة خلف النافذة
      // حُمّلت بأخطاء. تحميل كامل يعيدها سليمة بدل أن يُترك المستخدم معها.
      window.location.reload();
    } catch (cause) {
      setError(describeApiError(cause, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={t('password.mustChangeTitle')} onClose={() => undefined} locked>
      <div className="form-stack password-forced" data-testid="password-forced-modal">
        <div className="must-change">
          <AlertTriangle size={18} aria-hidden />
          <div>
            <p>{t('password.mustChangeBody')}</p>
          </div>
        </div>
        {error && <ErrorAlert message={error} />}
        <label>
          {t('password.current')}
          <PasswordInput
            dir="ltr"
            autoComplete="current-password"
            value={current}
            maxLength={128}
            autoFocus
            onChange={(event) => setCurrent(event.target.value)}
          />
        </label>
        <label>
          {t('password.new')}
          <PasswordInput
            dir="ltr"
            autoComplete="new-password"
            value={next}
            maxLength={128}
            onChange={(event) => setNext(event.target.value)}
          />
        </label>
        <label>
          {t('password.confirm')}
          <PasswordInput
            dir="ltr"
            autoComplete="new-password"
            value={confirm}
            maxLength={128}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </label>
        <div className="form-actions">
          <button type="button" className="button" disabled={busy} onClick={() => void save()}>
            <KeyRound size={15} /> {t('password.change')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
